import { FC, memo } from 'react';
import { useLocation } from 'react-router';

import { useStatic } from '~/common/hooks/useStatic';
import { resolveModuleFromPath } from './logic/resolveModuleFromPath';
import { ModuleScopeProviderProps } from './types/ModuleScopeProviderProps';
import { ModuleScopeContext } from './ModuleScopeContext';

/**
 * Resolves which module the tab is in from the current route, and provides it to
 * the tree. The module is fixed for a tab's lifetime (we never switch modules in
 * a tab), so it is cached from the first resolution. It throws if the initial
 * path matches no module, or if a later render resolves a different module (an
 * in-tab module switch, which is unsupported).
 */
const ModuleScopeProviderComponent: FC<ModuleScopeProviderProps> = ({
  moduleRouteConfigs,
  children,
}) => {
  const { pathname } = useLocation();
  const currentModule = resolveModuleFromPath(moduleRouteConfigs, pathname);

  // Cache the first resolution; hooks run before any throw so hook order is stable.
  const moduleScope = useStatic(currentModule);

  if (!moduleScope) {
    throw new Error(
      `ModuleScope: no module matches the initial path "${pathname}"`,
    );
  }

  if (currentModule !== moduleScope) {
    throw new Error(
      `ModuleScope: the module changed within a tab (${moduleScope} -> ${
        currentModule ?? 'none'
      }); switching modules in the same tab is not supported`,
    );
  }

  return (
    <ModuleScopeContext.Provider value={moduleScope}>
      {children}
    </ModuleScopeContext.Provider>
  );
};

export const ModuleScopeProvider = memo(ModuleScopeProviderComponent);
